import { useHasEnablePackPermissions } from "../hooks/useHasEnablePackPermissions";
import { useHasPurchasingPower } from "../hooks/useHasPurchasingPower";
import EnablePackModal from "./EnablePackModal";
import EnablePackTrialModal from "./EnablePackTrialModal";
import PackRequestModal from "./PackRequestModal";
import PackTrialRequestModal from "./PackTrialRequestModal";
import {
  type EnablePackModalPropsOwn,
  type EnableTrialModalPropsOwn,
  type PackRequestCommonProps,
  type PackRequestModalPropsOwn,
  type PackTrialRequestModalPropsOwn,
} from "./types";
import { noop } from "lodash";
import { PricingTypes } from "enums/licensing/pricingTypes";
import { useMemo } from "react";
import type { PaymentPack } from "../PaymentModal/types";
import SinglePaymentModalContainer from "../PaymentModal/container/SinglePaymentModalContainer";
import { useRequestData } from "../PackCard/hooks/useRequestData";

export interface Props
  extends Omit<PackRequestCommonProps, "isOpen">,
    Omit<PackRequestModalPropsOwn, "requestType" | "title">,
    EnablePackModalPropsOwn,
    PackTrialRequestModalPropsOwn,
    EnableTrialModalPropsOwn {
  showModal: boolean;
  isTrialRequest: boolean;
  onPaymentSuccess?: (id: number) => void;
  rawPricePerUser?: number;
  billingTerm?: string;
  isPurchased?: boolean;
  isTrial?: boolean;
  packPricingTypeId?: number;
  isExpired?: boolean;
}

export const RequestPackModals: React.FC<Props> = (props) => {
  const hasEnablePackPermissions = useHasEnablePackPermissions();
  const hasPurchasingPower = useHasPurchasingPower();
  const {
    onSubmitEnablePack,
    onSubmitPackRequest,
    onTrialRequest,
    onTrialEnable,
    onPaymentSuccess = noop,
    trialDuration,
    showModal,
    isTrialRequest,
    billingTerm = "Annual",
    rawPricePerUser = 0,
    packPricingTypeId,
    isExpired,
    ...commonBag
  } = props;

  const showPaymentModal = packPricingTypeId === PricingTypes.Paid && hasPurchasingPower && !isTrialRequest;
  const showRequestModal = !isTrialRequest && (!hasEnablePackPermissions || packPricingTypeId === PricingTypes.Other);
  const showEnableModal = packPricingTypeId === PricingTypes.Free && hasEnablePackPermissions;
  const requestData = useRequestData(!!commonBag.isTrial, !!isExpired, !!commonBag.isPurchased);

  const paymentPacks: PaymentPack[] = useMemo(
    () => [
      {
        packName: commonBag.packTitle,
        packId: commonBag.packId,
        isExpired: !!isExpired,
        isTrial: !!commonBag.isTrial,
        billingTerm,
        rawPricePerUser,
        isPurchased: commonBag.isPurchased ?? false,
      },
    ],
    [billingTerm, commonBag, isExpired, rawPricePerUser],
  );

  return (
    <>
      <EnablePackModal {...commonBag} onSubmitEnablePack={onSubmitEnablePack} isOpen={showModal && showEnableModal} />
      <PackRequestModal
        {...commonBag}
        requestType={requestData.type}
        title={requestData.title}
        onSubmitPackRequest={onSubmitPackRequest}
        isOpen={showModal && showRequestModal}
      />
      <PackTrialRequestModal
        {...commonBag}
        onTrialRequest={onTrialRequest}
        isOpen={showModal && isTrialRequest && !hasPurchasingPower}
        trialDuration={trialDuration}
      />
      <EnablePackTrialModal
        {...commonBag}
        onTrialEnable={onTrialEnable}
        isOpen={showModal && isTrialRequest && hasPurchasingPower}
        trialDuration={trialDuration}
      />
      {showModal && showPaymentModal && (
        <SinglePaymentModalContainer
          paymentPacks={paymentPacks}
          onPaymentSuccess={onPaymentSuccess}
          onClose={commonBag.onCancelPackRequest}
        />
      )}
    </>
  );
};
